import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { AdminLayout } from '@/components/AdminLayout';
import { Plus, FileText, Edit2, Trash2, ChevronDown, ChevronRight, Package, FolderPlus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';

const AdminTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    categories: [],
    is_active: true
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    products: []
  });

  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/templates`);
      setTemplates(res.data);
    } catch (e) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Informe o nome do template');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/templates/${editingTemplate.id}`, form);
        toast.success('Template atualizado!');
      } else {
        await axios.post(`${API}/admin/templates`, form);
        toast.success('Template criado!');
      }
      setShowModal(false);
      setEditingTemplate(null);
      setForm({ name: '', description: '', categories: [], is_active: true });
      fetchTemplates();
    } catch (e) {
      toast.error(editingTemplate ? 'Erro ao atualizar template' : 'Erro ao criar template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description || '',
      categories: template.categories || [],
      is_active: template.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/templates/${templateToDelete.id}`);
      toast.success('Template excluído!');
      setShowDeleteDialog(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao excluir template');
    }
  };

  const handleAddCategory = async () => {
    if (!categoryForm.name || !selectedTemplate) {
      toast.error('Informe o nome da categoria');
      return;
    }

    try {
      await axios.post(`${API}/admin/templates/${selectedTemplate.id}/categories`, categoryForm);
      toast.success('Categoria adicionada!');
      setShowCategoryModal(false);
      setCategoryForm({ name: '', products: [] });
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao adicionar categoria');
    }
  };

  const handleDeleteCategory = async (templateId, categoryName) => {
    try {
      await axios.delete(`${API}/admin/templates/${templateId}/categories/${encodeURIComponent(categoryName)}`);
      toast.success('Categoria excluída!');
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao excluir categoria');
    }
  };

  const handleAddProduct = async () => {
    if (!productForm.name || !productForm.price || !selectedTemplate || !selectedCategory) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      // Find template and update category with new product
      const template = templates.find(t => t.id === selectedTemplate.id);
      const updatedCategories = template.categories.map(cat => {
        if (cat.name === selectedCategory.name) {
          return {
            ...cat,
            products: [...(cat.products || []), {
              name: productForm.name,
              description: productForm.description,
              price: parseFloat(productForm.price)
            }]
          };
        }
        return cat;
      });

      await axios.put(`${API}/admin/templates/${selectedTemplate.id}/categories`, updatedCategories);
      toast.success('Produto adicionado!');
      setShowProductModal(false);
      setProductForm({ name: '', description: '', price: '' });
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao adicionar produto');
    }
  };

  const handleDeleteProduct = async (templateId, categoryName, productIndex) => {
    try {
      const template = templates.find(t => t.id === templateId);
      const updatedCategories = template.categories.map(cat => {
        if (cat.name === categoryName) {
          const newProducts = [...(cat.products || [])];
          newProducts.splice(productIndex, 1);
          return { ...cat, products: newProducts };
        }
        return cat;
      });

      await axios.put(`${API}/admin/templates/${templateId}/categories`, updatedCategories);
      toast.success('Produto removido!');
      fetchTemplates();
    } catch (e) {
      toast.error('Erro ao remover produto');
    }
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setForm({ name: '', description: '', categories: [], is_active: true });
    setShowModal(true);
  };

  const toggleTemplate = (id) => {
    setExpandedTemplates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <AdminLayout title="Templates">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Templates">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-white/60">{templates.length} templates disponíveis</p>
          <Button onClick={openNewTemplate} className="bg-[#ff6b00] hover:bg-[#ff8533]">
            <Plus className="w-4 h-4 mr-2" />
            Novo Template
          </Button>
        </div>

        <div className="space-y-4">
          {templates.map(template => (
            <Card key={template.id} className="card-adegga">
              <CardContent className="p-0">
                <Collapsible open={expandedTemplates[template.id]} onOpenChange={() => toggleTemplate(template.id)}>
                  <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3">
                      {expandedTemplates[template.id] ? (
                        <ChevronDown className="w-5 h-5 text-white/40" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-white/40" />
                      )}
                      <div className="w-10 h-10 rounded-lg bg-[#ff6b00]/20 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#ff6b00]" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-bold text-white">{template.name}</h3>
                        <p className="text-sm text-white/60">{template.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={template.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {template.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <span className="text-sm text-white/40">
                        {template.categories?.length || 0} categorias
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        onClick={(e) => { e.stopPropagation(); handleEdit(template); }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTemplateToDelete(template);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="px-4 pb-4 border-t border-white/10 pt-4">
                      {/* Add Category Button */}
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-white font-medium">Categorias e Produtos</h4>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#ff6b00]/50 text-[#ff6b00] hover:bg-[#ff6b00]/10"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setShowCategoryModal(true);
                          }}
                        >
                          <FolderPlus className="w-4 h-4 mr-1" />
                          Adicionar Categoria
                        </Button>
                      </div>

                      {/* Categories List */}
                      {template.categories?.length === 0 ? (
                        <p className="text-white/40 text-sm text-center py-4">Nenhuma categoria ainda</p>
                      ) : (
                        <div className="space-y-3">
                          {template.categories?.map((cat, catIndex) => (
                            <div key={catIndex} className="bg-[#0f0f15] rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-white font-medium">{cat.name}</span>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-[#ff6b00] hover:bg-[#ff6b00]/10"
                                    onClick={() => {
                                      setSelectedTemplate(template);
                                      setSelectedCategory(cat);
                                      setShowProductModal(true);
                                    }}
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Produto
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-red-400 hover:bg-red-500/10"
                                    onClick={() => handleDeleteCategory(template.id, cat.name)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* Products */}
                              <div className="space-y-1">
                                {cat.products?.map((prod, prodIndex) => (
                                  <div key={prodIndex} className="flex items-center justify-between py-1 px-2 bg-white/5 rounded text-sm">
                                    <div className="flex items-center gap-2">
                                      <Package className="w-3 h-3 text-white/40" />
                                      <span className="text-white/80">{prod.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-green-400">R$ {prod.price?.toFixed(2)}</span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                                        onClick={() => handleDeleteProduct(template.id, cat.name, prodIndex)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                {(!cat.products || cat.products.length === 0) && (
                                  <p className="text-white/30 text-xs py-1">Nenhum produto</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12 text-white/40">
            Nenhum template cadastrado
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Excluir Template</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white/60">
                Tem certeza que deseja excluir o template <strong className="text-white">{templateToDelete?.name}</strong>?
              </p>
              <p className="text-sm text-yellow-400 mt-2">Esta ação não pode ser desfeita.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Template Modal */}
        <Dialog open={showModal} onOpenChange={(open) => {
          setShowModal(open);
          if (!open) {
            setEditingTemplate(null);
            setForm({ name: '', description: '', categories: [], is_active: true });
          }
        }}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingTemplate ? 'Editar Template' : 'Novo Template'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Nome do template"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <Textarea
                placeholder="Descrição"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <span className="text-white/60">Ativo</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSave}>
                {editingTemplate ? 'Salvar' : 'Criar Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Category Modal */}
        <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Nova Categoria</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Nome da categoria (ex: Cervejas, Destilados)"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCategoryModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleAddCategory}>
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Product Modal */}
        <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                Novo Produto em {selectedCategory?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Nome do produto"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <Input
                placeholder="Descrição (opcional)"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Preço (R$)"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowProductModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleAddProduct}>
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTemplates;
